import getAllPostsFromAPI from './api/getAllPostsFromAPI';
import moment from 'moment';
import { getMediaTypeNameById } from '../utils/getMediaLabelsById';
import { decimal, decimalIgnoreNan, shortDate } from '../utils/formatters';
import downloadCSV from '../utils/downloadCsv';
import { capitalize } from '../utils/capitalize';
import Papa from 'papaparse';
import { CampaignsInsights } from '@app/types/api';
import classNames from '@app/ui/sports-components/utils/classNames';

const POSTS_DOWNLOAD_LIMIT = 20000;

// Youtube and Tiktok
const PLATFORMS_TO_NOT_REQUIRE_AUTH = new Set([4, 23]);

export const downloadAuthenticatedSocialPostsAsCsv = async (
    queryParams,
    clientId,
    csvDelimiter,
    sendNotification,
    t: (key: string) => string,
) => {
    const csvQueryParams = {
        ...queryParams,
        perPage: POSTS_DOWNLOAD_LIMIT,
        startIndex: 0,
    };
    const { posts, postCount } = await getAllPostsFromAPI(
        csvQueryParams,
        clientId,
    );

    if (postCount >= POSTS_DOWNLOAD_LIMIT) {
        sendNotification('Request exceeds the 20,000 posts.', {
            context: 'warning',
            description:
                'Please use filters to reduce the amount of data requested.',
        });
        return;
    }

    const getCarouselMediaType = (mediaTypes = []) => {
        if (mediaTypes.length > 1) {
            return 'Carousel - Images & Videos';
        }
        return `Carousel - ${getMediaTypeNameById(mediaTypes[0])}s`;
    };

    const postValues = posts.map(post => ({
        platformPostId: post.platformPostId,
        postType: post.postType,
        accountUsername: post.username,
        postUrl: post.url,
        createdAtDate: shortDate(post.createdAt),
        createdAtTime: moment(post.createdAt).format('hh:mm'),
        platform: getPlatformNameById(post.platformId),
        mediaType: post.carousel
            ? getCarouselMediaType(post.mediaTypeIds ?? [])
            : getMediaTypeNameById(post.mediaTypeId),
        caption: post.content,
        impressions: decimal(post.impressions, true),
        organicImpressions: decimalIgnoreNan(post.organicImpressions),
        paidImpressions: decimalIgnoreNan(post.paidImpressions),
        videoViews: decimalIgnoreNan(post.videoViews),
        organicVideoViews: decimalIgnoreNan(post.organicVideoViews),
        paidVideoViews: decimalIgnoreNan(post.paidVideoViews),
        engagements: decimalIgnoreNan(post.engagements),
        organicEngagements: decimalIgnoreNan(post.organicEngagements),
        paidEngagements: decimalIgnoreNan(post.paidEngagements),
        organicComments: decimalIgnoreNan(
            post.comments - (post.paidComments || 0),
        ),
        organicShares: decimalIgnoreNan(post.shares),
        organicSaves: decimalIgnoreNan(post.organicSaves),
        organicLikes: decimalIgnoreNan(post.organicLikes),
        organicReplies: decimalIgnoreNan(post.organicReplies || post.replies),
        organicRetweets: decimalIgnoreNan(post.organicRetweets),
        reactionsWow: decimalIgnoreNan(post.organicReactionsWows),
        reactionsLove: decimalIgnoreNan(post.organicReactionsLoves),
        reactionsLike: decimalIgnoreNan(post.organicReactionsLikes),
        reactionsHaha: decimalIgnoreNan(post.organicReactionsHahas),
        reactionsSad: decimalIgnoreNan(post.reactionsSorry),
        reactionsAnger: decimalIgnoreNan(post.reactionsAnger),
        tapsForward: decimalIgnoreNan(post.tapsForward),
        tapsBack: decimalIgnoreNan(post.tapsBack),
        exits: decimalIgnoreNan(post.exits),
        paidComments: decimalIgnoreNan(post.paidComments),
        paidShares: decimalIgnoreNan(post.paidShares),
        paidSaves: decimalIgnoreNan(post.paidSaves),
        paidLikes: decimalIgnoreNan(post.paidLikes),
        paidReplies: decimalIgnoreNan(post.paidReplies),
        paidRetweets: decimalIgnoreNan(post.paidRetweets),
        paidFbReactions: decimalIgnoreNan(post.paidReactions),
        fmv: decimal(post.fullMediaValue, true, false),
        socialCampaigns: post.socialCampaigns,
    }));

    const tableData = postValues.map(row => ({
        [t('ownedSocial:socialPostMetrics.columns.platformPostId')]:
            row.platformPostId,
        [t('ownedSocial:socialPostMetrics.columns.postType')]: row.postType,
        [t('ownedSocial:socialPostMetrics.columns.accountUsername')]:
            row.accountUsername,
        [t('ownedSocial:socialPostMetrics.columns.postUrl')]: row.postUrl,
        [t('ownedSocial:socialPostMetrics.columns.createdDate')]:
            row.createdAtDate,
        [t('ownedSocial:socialPostMetrics.columns.createdTime')]:
            row.createdAtTime,
        [t('ownedSocial:socialPostMetrics.columns.platform')]: row.platform,
        [t('ownedSocial:socialPostMetrics.columns.mediaType')]: row.mediaType,
        [t('ownedSocial:socialPostMetrics.columns.caption')]: row.caption,
        [t('ownedSocial:socialPostMetrics.columns.impressions')]:
            row.impressions,
        [t('ownedSocial:socialPostMetrics.columns.organicImpressions')]:
            row.organicImpressions,
        [t('ownedSocial:socialPostMetrics.columns.paidImpressions')]:
            row.paidImpressions,
        [t('ownedSocial:socialPostMetrics.columns.videoViews')]: row.videoViews,
        [t('ownedSocial:socialPostMetrics.columns.organicVideoViews')]:
            row.organicVideoViews,
        [t('ownedSocial:socialPostMetrics.columns.paidVideoViews')]:
            row.paidVideoViews,
        [t('ownedSocial:socialPostMetrics.columns.engagements')]:
            row.engagements,
        [t('ownedSocial:socialPostMetrics.columns.organicEngagements')]:
            row.organicEngagements,
        [t('ownedSocial:socialPostMetrics.columns.paidEngagements')]:
            row.paidEngagements,
        [t('ownedSocial:socialPostMetrics.columns.organicComments')]:
            row.organicComments,
        [t('ownedSocial:socialPostMetrics.columns.organicShares')]:
            row.organicShares,
        [t('ownedSocial:socialPostMetrics.columns.organicSaves')]:
            row.organicSaves,
        [t('ownedSocial:socialPostMetrics.columns.organicLikes')]:
            row.organicLikes,
        [t('ownedSocial:socialPostMetrics.columns.organicReplies')]:
            row.organicReplies,
        [t('ownedSocial:socialPostMetrics.columns.organicRetweets')]:
            row.organicRetweets,
        [t('ownedSocial:socialPostMetrics.columns.facebookReactionsWow')]:
            row.reactionsWow,
        [t('ownedSocial:socialPostMetrics.columns.facebookReactionsLove')]:
            row.reactionsLove,
        [t('ownedSocial:socialPostMetrics.columns.facebookReactionsLike')]:
            row.reactionsLike,
        [t('ownedSocial:socialPostMetrics.columns.facebookReactionsHaha')]:
            row.reactionsHaha,
        [t('ownedSocial:socialPostMetrics.columns.facebookReactionsSad')]:
            row.reactionsSad,
        [t('ownedSocial:socialPostMetrics.columns.facebookReactionsAngry')]:
            row.reactionsAnger,
        [t(
            'ownedSocial:socialPostMetrics.columns.instagramStoriesTapsForward',
        )]: row.tapsForward,
        [t('ownedSocial:socialPostMetrics.columns.instagramStoriesTapsBack')]:
            row.tapsBack,
        [t('ownedSocial:socialPostMetrics.columns.instagramStoriesExits')]:
            row.exits,
        [t('ownedSocial:socialPostMetrics.columns.paidComments')]:
            row.paidComments,
        [t('ownedSocial:socialPostMetrics.columns.paidShares')]: row.paidShares,
        [t('ownedSocial:socialPostMetrics.columns.paidSaves')]: row.paidSaves,
        [t('ownedSocial:socialPostMetrics.columns.paidLikes')]: row.paidLikes,
        [t('ownedSocial:socialPostMetrics.columns.paidReplies')]:
            row.paidReplies,
        [t('ownedSocial:socialPostMetrics.columns.paidRetweets')]:
            row.paidRetweets,
        [t('ownedSocial:socialPostMetrics.columns.paidFacebookReactions')]:
            row.paidFbReactions,
        [t('ownedSocial:socialPostMetrics.columns.fmv')]: row.fmv,
        [t('ownedSocial:socialPostMetrics.columns.socialCampaigns')]:
            row.socialCampaigns,
    }));

    const jsonToCsv = Papa.unparse(tableData, {
        delimiter: csvDelimiter,
    });

    const fileName = `all-social-posts`;
    downloadCSV(jsonToCsv, fileName);

    sendNotification('File downloaded!', { context: 'success' });
};

export const downloadCampaignsSummaryAsCsv = (
    campaignsInsights: CampaignsInsights,
    csvDelimiter,
    sendNotification,
    t: (key: string) => string,
) => {
    const rows = (campaignsInsights?.values ?? []).map(campaign => ({
        [t('ownedSocial:socialPostMetrics.columns.campaign')]:
            campaign.campaignName,
        [t('ownedSocial:socialPostMetrics.columns.impressions')]:
            decimalIgnoreNan(campaign.impressions),
        [t('ownedSocial:socialPostMetrics.columns.organicImpressions')]:
            decimalIgnoreNan(campaign.organicImpressions),
        [t('ownedSocial:socialPostMetrics.columns.paidImpressions')]:
            decimalIgnoreNan(campaign.paidImpressions),
        [t('ownedSocial:socialPostMetrics.columns.videoViews')]:
            decimalIgnoreNan(campaign.videoViews),
        [t('ownedSocial:socialPostMetrics.columns.organicVideoViews')]:
            decimalIgnoreNan(campaign.organicVideoViews),
        [t('ownedSocial:socialPostMetrics.columns.paidVideoViews')]:
            decimalIgnoreNan(campaign.paidVideoViews),
        [t('ownedSocial:socialPostMetrics.columns.engagements')]:
            decimalIgnoreNan(campaign.engagements),
        [t('ownedSocial:socialPostMetrics.columns.organicEngagements')]:
            decimalIgnoreNan(campaign.organicEngagements),
        [t('ownedSocial:socialPostMetrics.columns.paidEngagements')]:
            decimalIgnoreNan(campaign.paidEngagements),
        [t('ownedSocial:socialPostMetrics.columns.fullMediaValue')]: decimal(
            campaign.fullMediaValue,
            true,
            false,
        ),
        [t('ownedSocial:socialPostMetrics.columns.postCount')]:
            decimalIgnoreNan(campaign.postCount),
    }));
    const jsonToCsv = Papa.unparse(rows, {
        delimiter: csvDelimiter,
    });

    const fileName = `campaigns-summary`;
    downloadCSV(jsonToCsv, fileName);

    sendNotification('File downloaded!', { context: 'success' });
};

export const getPlatformAccountMap = clientAccounts =>
    clientAccounts.reduce((acc, currentAccount) => {
        const account = currentAccount.account;
        const platformAccountKey = `${account.platformId}-${account.id}`;
        acc[platformAccountKey] = currentAccount;
        return acc;
    }, {});

export const getFilteredOnoAccounts = response => {
    return (Array.isArray(response) ? response : []).flatMap(rh =>
        (Array.isArray(rh?.accounts) ? rh.accounts : [])
            .filter(a => PLATFORMS_TO_NOT_REQUIRE_AUTH.has(a.platformId))
            .map(account => ({ account })),
    );
};

export const getAccountOptionsFromResponse = response => {
    return response.reduce((accounts, currentAccount) => {
        const { id, username, platformId } = currentAccount.account;

        if (accounts[platformId]) {
            accounts[platformId].nested.push({
                label: username,
                value: id,
            });
        }

        if (!accounts[platformId]) {
            accounts[platformId] = {
                label: capitalize(getPlatformNameById(platformId)),
                value: platformId,
                nested: [
                    {
                        label: username,
                        value: id,
                    },
                ],
            };
        }
        return accounts;
    }, {});
};

export const removeCampaignFromPost = (postsList, postId, campaignId) =>
    postsList.map(post => {
        if (post.id === postId) {
            post.campaigns = (post.campaigns ?? []).filter(
                c => c.id !== campaignId,
            );
        }
        return post;
    });

export const getPlatformNameById = (platformId: number) =>
    ({
        1: 'x',
        2: 'instagram',
        3: 'facebook',
        4: 'youtube',
        5: 'broadcast',
        23: 'tiktok',
    })[platformId];

export const getPlatformIconClasses = platformId => {
    const lowerSocNam =
        platformId == 1 ? 'x-twitter' : getPlatformNameById(platformId);
    return classNames(
        'photo-stream__card-block-social-icon',
        'fa',
        'fa-fw',
        'fab-fa6',
        {
            [`fa-${lowerSocNam}`]: lowerSocNam,
        },
    );
};
